import React, { useState } from 'react';
import { Box, Button, ButtonBase, IconButton, Popover, Stack, TextField, Tooltip, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import NotificationAddOutlinedIcon from '@mui/icons-material/NotificationAddOutlined';
import { shortDate } from '../helpers';
import { launchColors } from '../launchTheme';
import { YearbookRow } from '../types';
import EditableCell from './EditableCell';
import { sampleCount, SampleTotal } from './sampleTotals';

export type ReminderPatch = { sampleReminderDate: string | null } | { sampleReminderDone: boolean };

interface Props {
  row: YearbookRow;
  total?: SampleTotal;
  editable: boolean;
  countLocked?: boolean;
  asOf: string;
  onSave: (value: string) => Promise<boolean>;
  onReminder: (patch: ReminderPatch) => Promise<boolean>;
}

const pillSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  px: 1,
  height: 22,
  borderRadius: '11px',
  fontSize: 13,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
};

const totalText = (total: SampleTotal) => {
  const parts = total.parts.map((part) => `${part.chain} ${part.count}`).join(' · ');
  const unread = total.unreadable ? ` · ${total.unreadable} rad${total.unreadable === 1 ? '' : 'er'} utan antal` : '';
  return `Totalt ${total.total} i samma aviseringsvecka: ${parts}${unread}`;
};

const SamplesValue: React.FC<{ value: string; total?: SampleTotal }> = ({ value, total }) => {
  const count = sampleCount(value);
  if (count === null) return <>{value}</>;
  return (
    <Box component="span" sx={{ ...pillSx, bgcolor: launchColors.softNavy, color: launchColors.navy }}>
      {count}
      {total && (
        <Tooltip title={totalText(total)}>
          <Box component="span" aria-label={totalText(total)} sx={{ fontWeight: 600, color: launchColors.textSecondary }}>({total.total})</Box>
        </Tooltip>
      )}
    </Box>
  );
};

const SamplesCell: React.FC<Props> = ({ row, total, editable, countLocked = false, asOf, onSave, onReminder }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const date = row.sampleReminderDate || null;
  const done = Boolean(date && row.sampleReminderDone);
  const due = Boolean(date && !done && date <= asOf);

  const open = (target: HTMLElement) => {
    setDraft(date || '');
    setAnchor(target);
  };

  const apply = async (patch: ReminderPatch) => {
    setSaving(true);
    try {
      if (await onReminder(patch)) setAnchor(null);
    } finally {
      setSaving(false);
    }
  };

  const reminder = date ? (
    <ButtonBase
      disabled={!editable}
      onClick={(event) => open(event.currentTarget)}
      aria-label={done ? `Varuprov skickat, påminnelse ${shortDate(date, asOf)}` : `Påminnelse ${shortDate(date, asOf)}${due ? ', dags nu' : ''}`}
      sx={{
        ...pillSx,
        flexShrink: 0,
        fontSize: 12,
        bgcolor: done ? launchColors.mossLight : due ? launchColors.dangerBg : launchColors.petrolLight,
        color: done ? launchColors.mossDark : due ? launchColors.dangerText : launchColors.petrolDark,
        border: due ? `1px solid ${launchColors.dangerText}` : '1px solid transparent',
      }}
    >
      {done ? <CheckCircleOutlineIcon sx={{ fontSize: 15 }} /> : <NotificationsActiveOutlinedIcon sx={{ fontSize: 15 }} />}
      {done ? 'Skickat' : shortDate(date, asOf)}
    </ButtonBase>
  ) : editable ? (
    <Tooltip title="Påminn mig om varuprovet">
      <IconButton size="small" aria-label="Lägg till påminnelse om varuprov" onClick={(event) => open(event.currentTarget)} sx={{ flexShrink: 0, p: 0.25, color: launchColors.textMuted }}>
        <NotificationAddOutlinedIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  ) : null;

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <EditableCell
          value={row.samples}
          label="Varuprover"
          editable={editable && !countLocked}
          maxLength={500}
          display={<SamplesValue value={row.samples} total={total} />}
          onSave={onSave}
        />
      </Box>
      {reminder}
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Stack spacing={1.5} sx={{ p: 2, width: 280 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: launchColors.navy }}>Påminnelse om varuprov</Typography>
          <TextField
            type="date"
            size="small"
            label="Påminn mig"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>
            Den dagen får fliken Årskalender en röd siffra. Påminnelsen går till den som sparar datumet.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="contained" size="small" disabled={saving || !draft || (draft === date && !done)} onClick={() => { void apply({ sampleReminderDate: draft }); }}>
              Spara
            </Button>
            {date && !done && (
              <Button size="small" color="success" disabled={saving} onClick={() => { void apply({ sampleReminderDone: true }); }}>Skickat</Button>
            )}
            {done && (
              <Button size="small" disabled={saving} onClick={() => { void apply({ sampleReminderDone: false }); }}>Inte skickat</Button>
            )}
            {date && (
              <Button size="small" color="inherit" disabled={saving} onClick={() => { void apply({ sampleReminderDate: null }); }}>Ta bort</Button>
            )}
          </Stack>
        </Stack>
      </Popover>
    </Stack>
  );
};

export default SamplesCell;
